import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useField } from 'formik';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { pick, is } from 'ramda';

import {
  RIGHT,
  LEFT,
  DATE,
  COMMENT,
  PUPIL_SIZE,
  SPHERE,
  CYLINDER,
  AXIS,
  CONFIDENCE,
  MOBILITY,
  VISUAL_ACUITY,
  useExaminationsActions,
  useAlerts,
} from 'store/examinations';
import FileDropZone from 'components/FileDropZone';
import { mapPropsToValues } from '../utils';
import ContainerWithHistory from '../ContainerWithHistory';

import Section from './Section';
import Dropdowns from './Dropdowns';
import Input from './Input';
import History from './History';
import { Container } from './styles';

const VisualAcuityForm = ({ side, readMode, resetForm }) => {
  const { id } = useParams();
  const { uploadXML } = useExaminationsActions();
  const { action, loading, success, resetAlerts } = useAlerts(uploadXML);
  const [isOpened, setOpened] = useState(false);
  const getPath = useCallback((item) => [side, VISUAL_ACUITY, item].join('.'), [side]);
  const [pupilSize, { error: pupilSizeErr }] = useField(getPath(PUPIL_SIZE));
  const [sphere, { error: sphereErr }] = useField(getPath(SPHERE));
  const [cylinder, { error: cylinderErr }] = useField(getPath(CYLINDER));
  const [axis, { error: axisErr }] = useField(getPath(AXIS));
  const [confidence, { error: confidenceErr }] = useField(getPath(CONFIDENCE));

  const highlightAutorefraction = useMemo(
    () =>
      !readMode &&
      (!confidence.value || confidence.value === 0 || confidence.value === '0') &&
      Boolean(sphere.value || cylinder.value || axis.value),
    [readMode, axis.value, confidence.value, cylinder.value, sphere.value]
  );

  const onDrop = useCallback(
    (files) => {
      if (!loading && files.length) action({ patientId: id, files });
    },
    [id, loading, action]
  );

  useEffect(() => {
    if (is(Object, success)) {
      resetForm({
        values: mapPropsToValues(pick([RIGHT, LEFT, DATE, COMMENT, MOBILITY], success)),
        errors: {},
      });
    }
    if (success) {
      resetAlerts();
    }
  }, [resetAlerts, resetForm, success]);

  return (
    <ContainerWithHistory label={`Sehschärfe ${side === RIGHT ? 'OD' : 'OS'}`} isOpened={isOpened} setOpened={setOpened}>
      <Container>
        <Dropdowns side={side} readMode={readMode} />
        <Section label="Pupillengr.">
          <Input field={pupilSize} readMode={readMode} error={pupilSizeErr} />
        </Section>
        <Section label="Autorefraktion:">
          <Input field={sphere} label="Sphäre" readMode={readMode} isRed={highlightAutorefraction} error={sphereErr} />
          <Input field={cylinder} label="Cylinder" readMode={readMode} isRed={highlightAutorefraction} error={cylinderErr} />
          <Input field={axis} label="Achse" readMode={readMode} isRed={highlightAutorefraction} error={axisErr} />
          <Input
            field={confidence}
            label="Messqualität"
            readMode={readMode}
            isRed={highlightAutorefraction}
            error={confidenceErr}
          />
        </Section>
      </Container>
      <History side={side} isOpened={isOpened} />
      {readMode || (
        <FileDropZone
          onDrop={onDrop}
          accept={['text/xml']}
          loading={loading}
          label="XML-Datei in dieses"
          acceptFormats={['.xml']}
        />
      )}
    </ContainerWithHistory>
  );
};

VisualAcuityForm.propTypes = {
  side: PropTypes.oneOf([RIGHT, LEFT]).isRequired,
  readMode: PropTypes.bool.isRequired,
  resetForm: PropTypes.func.isRequired,
};

export default VisualAcuityForm;
